/**
 * WINDOW SCROLL MODULE
 *
 * This module scrolls the window to a target via a click on an element with a [data-window-scroll] property.
 *
 * @param {[Object]} data-window-scroll
 * [
 *  target: [Required] HTML Element. Element to scroll to.
 *  offset: [Optional] Number | HTML Element. If parameter is a number, then the number will used for offset. If it is a HTML Element, than the offsetHeight of the element will be used.
 *  duration: [Optional] Number. Duration of the scroll.
 *  callback: [Optional] Function. This is called once the scroll is complete.
 * ]
 */

import { callbackOnElements, scrollToPosition } from '@borngroup/born-utilities';
import { breakpointRanges } from '../helpers/utilities';

export default class WindowScroll {
    constructor( query ) {
        callbackOnElements(query, this.setupWindowScroll.bind(this));

        this.breakpoints = breakpointRanges;
    }

    static setupScroll(destination, duration = 350, manualOffset = 0, callback, containerEl) {
        let scrollContainer = typeof containerEl === 'string' ? document.querySelector(containerEl) : containerEl,
            scrollOptions = {
                easings: {
                    easeOutCubic(t) {
                        return (--t) * t * t + 1;
                    }
                },
                startPosition: WindowScroll.getScrollPosition(scrollContainer),
                startTime: 'now' in window.performance ? performance.now() : new Date().getTime(),
                duration: duration,
                destinationPosition: scrollToPosition(destination, manualOffset, scrollContainer),
                callback: callback || function() {
                    return true;
                }
            };

        if ('requestAnimationFrame' in window === false) {
            (scrollContainer || window).scroll(0, scrollOptions.destinationPosition);

            if (typeof callback === 'function') {
                callback(scrollOptions);
            }

            return;
        }

        WindowScroll.easeScroll(scrollContainer, scrollOptions);
    }

    static getScrollPosition(scrollContainer) {
        return scrollContainer ? scrollContainer.scrollTop : window.pageYOffset;
    }

    static easeScroll(scrollContainer, scrollOptions) {
        let now = 'now' in window.performance ? performance.now() : new Date().getTime(),
            elapsedTime = now - scrollOptions.startTime,
            time = Math.min(1, (elapsedTime / scrollOptions.duration)),
            timeFunction = scrollOptions.easings['easeOutCubic'](time),
            currentScrollPos;

        (scrollContainer || window).scroll(0, Math.ceil((timeFunction * (scrollOptions.destinationPosition - scrollOptions.startPosition)) + scrollOptions.startPosition));

        currentScrollPos = WindowScroll.getScrollPosition(scrollContainer);

        //Check for both the elapsed time and the current position.
        //If either matches, stop the scrolling function.
        if (currentScrollPos === scrollOptions.destinationPosition || elapsedTime >= scrollOptions.duration) {
            scrollOptions.callback();

            return;
        }

        window.requestAnimationFrame(WindowScroll.easeScroll.bind(this, scrollContainer, scrollOptions));
    }

    isEnabled(breakpointName) {
        let matchedBreakpoint = this.breakpoints[breakpointName];

        return matchedBreakpoint && (!matchedBreakpoint.min || document.body.offsetWidth >= matchedBreakpoint.min) && (!matchedBreakpoint.max || document.body.offsetWidth <= matchedBreakpoint.max);
    }

    getOffsetValue(offsetMatch) {
        return offsetMatch instanceof HTMLElement ? offsetMatch.getBoundingClientRect().height : (offsetMatch || 0);
    }

    bindEvents(el) {
        let settings = JSON.parse( el.getAttribute( 'data-window-scroll' ) );

        el.addEventListener('click', function() {
            let enabled = settings.hasOwnProperty('enabled') ? this.isEnabled.call(this, settings.enabled) : true,
                delay = settings.delay || 0;

            window.setTimeout(function() {
                if (enabled && !(el.disabled || el.classList.contains('disabled'))) {
                    WindowScroll.setupScroll(settings.target, settings.duration, settings.offset, null, settings.scrollContainer);
                }
            }, delay);
        }.bind(this));

        el.removeAttribute('data-window-scroll');
    }

    setupWindowScroll(el) {
        this.bindEvents(el);
    }
}
